<script lang="ts" setup>
import Skeleton from 'primevue/skeleton';

interface SkeletonItem {
  width?: string;
  height?: string;
  size?: string;
  shape?: 'rectangle' | 'circle';
  borderRadius?: string;
  class?: string;
}

interface Props {
  items?: SkeletonItem[];
  variant?: 'rectangle' | 'rounded' | 'square' | 'circle';
  layout?: 'vertical' | 'horizontal';
  title?: string;
  containerClass?: string;
}

const {
  items = [],
  variant = 'rectangle',
  layout = 'vertical',
  title = '',
  containerClass = 'p-4',
} = defineProps<Props>();

const getDefaultItems = (variant: string): SkeletonItem[] => {
  switch (variant) {
    case 'rectangle':
      return [
        { class: 'mb-2' },
        { width: '10rem', class: 'mb-2' },
        { width: '5rem', class: 'mb-2' },
        { height: '2rem', class: 'mb-2' },
        { width: '10rem', height: '4rem' },
      ];
    case 'rounded':
      return [
        { class: 'mb-2', borderRadius: '16px' },
        { width: '10rem', class: 'mb-2', borderRadius: '16px' },
        { width: '5rem', borderRadius: '16px', class: 'mb-2' },
        { height: '2rem', class: 'mb-2', borderRadius: '16px' },
        { width: '10rem', height: '4rem', borderRadius: '16px' },
      ];
    case 'square':
      return [
        { size: '2rem', class: 'mr-2' },
        { size: '3rem', class: 'mr-2' },
        { size: '4rem', class: 'mr-2' },
        { size: '5rem' },
      ];
    case 'circle':
      return [
        { shape: 'circle', size: '2rem', class: 'mr-2' },
        { shape: 'circle', size: '3rem', class: 'mr-2' },
        { shape: 'circle', size: '4rem', class: 'mr-2' },
        { shape: 'circle', size: '5rem' },
      ];
    default:
      return [];
  }
};

const skeletonItems = computed(() => {
  return items.length > 0 ? items : getDefaultItems(variant);
});

const containerClasses = computed(() => {
  const baseClasses = containerClass;
  if (
    layout === 'horizontal' &&
    (variant === 'square' || variant === 'circle')
  ) {
    return `${baseClasses} flex items-end`;
  }
  return baseClasses;
});
</script>

<template>
  <div :class="containerClasses">
    <h5
      v-if="title"
      :class="{ 'mt-4': variant === 'square' || variant === 'circle' }"
    >
      {{ title }}
    </h5>
    <div
      v-if="
        layout === 'horizontal' &&
        (variant === 'square' || variant === 'circle')
      "
      class="flex items-end"
    >
      <Skeleton
        v-for="(item, index) in skeletonItems"
        :key="index"
        :width="item.width"
        :height="item.height"
        :size="item.size"
        :shape="item.shape"
        :border-radius="item.borderRadius"
        :class="item.class"
      />
    </div>
    <template v-else>
      <Skeleton
        v-for="(item, index) in skeletonItems"
        :key="index"
        :width="item.width"
        :height="item.height"
        :size="item.size"
        :shape="item.shape"
        :border-radius="item.borderRadius"
        :class="item.class"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped></style>
